import React, { useState } from "react";
import { Divider, MenuItem, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import { postNotification } from "../../../../components/utilities/Notifications";
import StyledMenu from "../../../../components/layout/StyledMenu";
import ExportModal from "../../../../components/modals/ExportModal";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import { tabIndexes } from "../../EventOrganize";
import {
  deleteData,
  isUsingWS,
  postData
} from "../../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { refreshPairings } from "../EventFunctions";
import PrintPairingsModal from "../../../../components/modals/PrintPairingsModal";

export function OptionsMenu(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });

  const {
    anchorEl,
    setAnchorEl,
    event,
    roundNo,
    setEvent,
    setRoundTimerOptions,
    setTopCutOptions,
    setCreatePairingModal,
    setRefreshPairings,
    setLoading,
    authContext,
    handleTabChange,
    setResetModalOpen
  } = props;
  const confirm = useConfirm();
  const open = Boolean(anchorEl);
  const [rosterModal, setRosterModal] = useState(false);
  const [printModal, setPrintModal] = useState(false);

  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        {event?.format?.leagueEvent && (
          <>
            <MenuItem
              onClick={(e) => {
                setCreatePairingModal(true);
                setAnchorEl(null);
              }}
              disableRipple
            >
              {t("createPairing")}
            </MenuItem>

            <Divider
              sx={{
                mt: 2,
                mb: 2
              }}
            />
          </>
        )}

        <MenuItem
          onClick={(e) => {
            setRoundTimerOptions(event);
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("setUpRoundTimer")}
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setLoading(true);

            try {
              await postData(`/events/${event.id}/sendPairingsNotification`, {
                body: {
                  email: authContext?.authState?.identity?.email
                }
              });

              postNotification("success", t("pairingsNotificationsSent"));
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("sendPairingsNotification")}
        </MenuItem>
        {!event?.topCut &&
          !event?.singleElimination &&
          !event?.doubleElimination && (
            <MenuItem
              onClick={(e) => {
                setTopCutOptions(event);
                setAnchorEl(null);
              }}
              disableRipple
            >
              {t("topCut")}
            </MenuItem>
          )}
        <MenuItem
          onClick={async (e) => {
            setLoading(true);

            try {
              await deleteData(`/events/${event.id}/clearCache`);
              setRefreshPairings(true);
              postNotification("success", t("eventCacheCleared"));
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("clearEventCache")}
        </MenuItem>

        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);

            try {
              await confirm({
                title: t("confirmDefaults"),
                description: t("confirmDefaultsDescription"),
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }

            setLoading(true);
            try {
              await postData(`/events/${event.id}/processDefaultSoftScores`);
              setRefreshPairings(true);
              postNotification("success", t("unsubmittedSoftScoresToDefaults"));
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
          }}
          disableRipple
        >
          {t("setAllUnsubmittedSoftScoresToDefaults")}
        </MenuItem>

        <MenuItem
          onClick={() => {
            refreshPairings();
          }}
        >
          {t("refreshPairings")}
        </MenuItem>

        {event.flags?.requirePairingPublish &&
          roundNo === event.status?.currentRound && (
            <MenuItem
              onClick={async () => {
                try {
                  await confirm({
                    title: t("areYouSure"),
                    description: t("unpublishPairingsConfirmation"),
                    dialogProps: {
                      sx: {
                        zIndex: 99999
                      }
                    },
                    confirmationText: t("yes"),
                    cancellationText: t("no")
                  });
                } catch (err) {
                  return;
                }
                setLoading(true);
                try {
                  await postData(`/events/${event.id}/unPublishPairings`, {
                    body: {
                      round: roundNo
                    }
                  });
                } catch (err) {
                  postNotification("error", err);
                }
                setLoading(false);
              }}
            >
              {t("unpublishPairings")}
            </MenuItem>
          )}

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          onClick={(e) => {
            setPrintModal(true);
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("printerFriendlyPairings")}
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            setRosterModal(true);
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("exportPairings")}
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            if (
              authContext?.authState?.identity?.isAdmin !== true &&
              !event?.userRole?.permissions?.circuitSubmission &&
              !authContext?.authState?.identity?.adminGameSystems?.includes(
                event?.gameSystem?.id
              )
            ) {
              postNotification("error", t("noAccessToEndTournament"));
              return;
            }

            try {
              await confirm({
                title: t("confirmEndTournament"),
                description: t("confirmEndTournamentDescription"),
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }
            setLoading(true);
            try {
              var updatedEvent = await postData(`/events/${event.id}`, {
                body: {
                  set: {
                    ended: true
                  }
                }
              });
              setEvent({ ...event, ...updatedEvent });
              handleTabChange(null, tabIndexes.placings, updatedEvent);
            } catch (err) {
              postNotification("error", err);
            }
            setLoading(false);
          }}
          disableRipple
        >
          {t("endTournament")}
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />
        <MenuItem
          onClick={async (e) => {
            setResetModalOpen(true);
            setAnchorEl(null);
          }}
          disableRipple
          sx={{ color: "error.main" }}
        >
          {t("resetTo")}
        </MenuItem>
      </StyledMenu>
      <ExportModal
        label={t("exportPairings")}
        exportTypes={["pairings"]}
        event={event}
        setLoading={setLoading}
        open={rosterModal}
        setOpen={setRosterModal}
        authContext={authContext}
      />
      {event && roundNo > 0 ? (
        <PrintPairingsModal
          label={t("printPairings")}
          event={event}
          round={roundNo}
          open={printModal}
          setOpen={setPrintModal}
        />
      ) : (
        <></>
      )}
    </>
  );
}

export function TeamPairingOptionsMenu(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });
  const { anchorEl, setAnchorEl, setSwapTeamModal } = props;
  const open = Boolean(anchorEl);
  return (
    <StyledMenu
      id="pairing-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      <MenuItem
        onClick={(e) => {
          setSwapTeamModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        {t("swapTeam")}
      </MenuItem>
    </StyledMenu>
  );
}

export function SeatedPairingOptionsMenu(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });
  const {
    anchorEl,
    setAnchorEl,
    setSwapSeatedPairingModal,
    setSwapSeatNumberModal
  } = props;
  const open = Boolean(anchorEl);
  return (
    <StyledMenu
      id="pairing-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      <MenuItem
        onClick={(e) => {
          setSwapSeatedPairingModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        {t("moveToDifferentTable")}
      </MenuItem>
      <MenuItem
        onClick={(e) => {
          setSwapSeatNumberModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        {t("changeSeatNumber")}
      </MenuItem>
    </StyledMenu>
  );
}

export function PairingOptionsMenu(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });
  const {
    event,
    player,
    anchorEl,
    setAnchorEl,
    setArmyModalOpen,
    setSwapPlayerModal,
    setRefreshPairings
  } = props;
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const confirm = useConfirm();
  const [processing, setProcessing] = useState(false);
  return (
    <StyledMenu
      id="pairing-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      {player?.id && (
        <>
          {player && getEnvironmentVariable("COMPANY_FLAG") !== "solforge" && (
            <MenuItem
              onClick={(e) => {
                setArmyModalOpen(true);
                setAnchorEl(null);
              }}
              disableRipple
            >
              {player?.armyListObjectId
                ? t("editList", {
                    listLabel: event?.gameSystem?.listLabel || "List"
                  })
                : t("addList", {
                    listLabel: event?.gameSystem?.listLabel || "List"
                  })}
            </MenuItem>
          )}

          {player?.armyListObjectId && (
            <MenuItem
              href={
                player?.armyListSolforge?.id
                  ? `${
                      getEnvironmentVariable("HEADER_SOLFORGE_URL") || ""
                    }/fused/${player.armyListSolforge.id}`
                  : `/list/${player.armyListObjectId}`
              }
              target="_blank"
              component="a"
              disableRipple
            >
              {t("viewList", {
                listLabel: event?.gameSystem?.listLabel || "List"
              })}
            </MenuItem>
          )}
        </>
      )}
      <MenuItem
        onClick={(e) => {
          setSwapPlayerModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        {t("swapPlayer")}
      </MenuItem>
      {player?.id && !player?.dropped && (
        <MenuItem
          sx={{
            color: theme?.palette?.error.main
          }}
          onClick={async (e) => {
            setAnchorEl(null);
            try {
              await confirm({
                title: t("confirmDrop"),
                description: t("confirmDropDescription"),
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }

            setProcessing(true);
            try {
              await postData(`/players/${player?.id}`, {
                body: {
                  set: {
                    dropped: true
                  }
                }
              });
              setRefreshPairings(true);
            } catch (err) {
              postNotification("error", err);
            }
            setProcessing(false);
          }}
          disableRipple
        >
          {t("dropPlayer")}
        </MenuItem>
      )}
      {processing && <ModalLoadingFull />}
    </StyledMenu>
  );
}

export function PairingPopUpOptionsMenu(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });

  const {
    anchorEl,
    setAnchorEl,
    setLoading,
    setRefreshPairings,
    pairing,
    setPairing,
    event,
    authContext,
    setSwapTableModal
  } = props;

  const open = Boolean(anchorEl);
  const confirm = useConfirm();

  const useWebSockets = isUsingWS(authContext);

  return (
    <StyledMenu
      id="pairingpopup-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      {pairing?.pairingType?.toLowerCase() === "pairing" &&
        Boolean(
          event.scorecardId &&
            event.gameSystem?.scorecardId &&
            event.scorecardId !== event.gameSystem.scorecardId
        ) && (
          <MenuItem
            component="a"
            href={`/game/${
              pairing?.pairingType
                ? pairing.pairingType.toLowerCase()
                : "pairing"
            }/${pairing?.id}?useGSD=true`}
            disableRipple
          >
            {getEnvironmentVariable("LABEL_GAMESYSTEM")} {t("scorecard")}
          </MenuItem>
        )}
      <MenuItem
        onClick={(e) => {
          setSwapTableModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        {t("changeTable", {
          keyPrefix: "misc.pairings"
        })}
      </MenuItem>
      <MenuItem
        onClick={async (e) => {
          try {
            await confirm({
              title: t("confirmClearScores"),
              description: t("confirmClearScoresDescription"),
              dialogProps: {
                sx: {
                  zIndex: 99999
                }
              }
            });
          } catch (err) {
            return;
          }
          try {
            setLoading(true);
            await postData(`/pairings/${pairing.id}/clearScores`, {
              body: {
                pairingType: pairing?.pairingType || "Pairing"
              }
            });
            setRefreshPairings(true);
            postNotification("success", t("scoresCleared"));
            setLoading(false);
            setPairing(null);
            setAnchorEl(null);
          } catch (err) {
            postNotification("error", err);
            setLoading(false);
          }
        }}
        disableRipple
      >
        {t("clearScores")}
      </MenuItem>

      <MenuItem
        onClick={async (e) => {
          setLoading(true);

          var metricDict = {
            gameResult: "gameResult",
            gamePoints: "gamePoints"
          };
          var body = {
            isTO: true,
            pairingType: pairing?.pairingType || "Pairing",
            isDone: true,
            metaData: {},
            gameData: {
              metrics: []
            }
          };
          if (authContext?.authState?.identity?.userId) {
            body.userId = authContext?.authState?.identity?.userId;
          }
          if (pairing?.metaData) {
            Object.keys(pairing.metaData).forEach((key) => {
              if (
                pairing.metaData[key] !== "" &&
                (typeof pairing.metaData[key] === "number" ||
                  !isNaN(Number(pairing.metaData[key])))
              ) {
                body.metaData[key] = "0";
              } else {
                body.metaData[key] = "";
              }
            });
          }

          let playerNo = 1;
          if (pairing.seatedPlayers?.length) {
            for (let seatedPlayer of pairing.seatedPlayers) {
              let player = `player${playerNo}`;
              body[`player${playerNo}Id`] = seatedPlayer.playerId;
              body.gameData[player] = {
                gameResult: 0,
                gamePoints: 0
              };
              if (pairing.games?.[seatedPlayer.playerId]) {
                for (let key of Object.keys(
                  pairing.games[seatedPlayer.playerId]
                )) {
                  metricDict[key] = key;
                  body.gameData[player][key] = 0;
                }
              }
              playerNo++;
            }
          } else {
            while (
              pairing &&
              pairing[
                `${body.pairingType === "TeamPairing" ? "teamPlayer" : "player"}${playerNo}`
              ]
            ) {
              let player = `player${playerNo}`;
              body.gameData[player] = {
                gameResult: 0,
                gamePoints: 0
              };
              if (pairing[player]?.game) {
                for (let key of Object.keys(pairing[player].game)) {
                  metricDict[key] = key;
                  body.gameData[player][key] = 0;
                }
              }
              playerNo++;
            }
          }

          Object.keys(metricDict).forEach((key) => {
            body.gameData.metrics.push(key);
          });

          try {
            setLoading(true);
            await postData(`/pairings/${pairing.id}/submitScores`, {
              body: body
            });

            if (!useWebSockets) setRefreshPairings(true);

            postNotification("success", t("pairingUpdated"));
            setLoading(false);
            setPairing(null);
            setAnchorEl(null);
          } catch (err) {
            postNotification("error", err);
            setLoading(false);
          }
        }}
        disableRipple
      >
        {pairing?.pairingType?.toLowerCase() === "teampairing"
          ? t("giveBothTeamsLosses")
          : pairing?.pairingType?.toLowerCase() === "seatedpairing"
            ? t("giveAllPlayersLosses")
            : t("giveBothAllPlayersLosses")}
      </MenuItem>

      <MenuItem
        onClick={async () => {
          try {
            await confirm({
              title: t("confirmDeletePairing"),
              description: t("confirmDeletePairingDescription"),
              dialogProps: {
                sx: {
                  zIndex: 99999
                }
              }
            });
          } catch (err) {
            return;
          }
          try {
            setLoading(true);
            await deleteData(`/pairings/${pairing.id}`, {
              body: {
                pairingType: pairing?.pairingType || "Pairing"
              }
            });
            setRefreshPairings(true);
            postNotification("success", t("pairingDeleted"));
            setLoading(false);
            setPairing(null);
            setAnchorEl(null);
          } catch (err) {
            postNotification("error", err);
            setLoading(false);
          }
        }}
        disableRipple
      >
        {pairing?.pairingType?.toLowerCase() === "teampairing"
          ? t("deleteTeamPairing")
          : t("deletePairing")}
      </MenuItem>
    </StyledMenu>
  );
}

export function FiltersMenu(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });

  const { anchorEl, setAnchorEl, event, filterType, setFilterType } = props;

  const open = Boolean(anchorEl);

  return (
    <>
      <StyledMenu
        id="filters-menu"
        MenuListProps={{
          "aria-labelledby": "filters-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          selected={filterType === "" ? true : false}
          onClick={async (e) => {
            setFilterType("");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("allPairings")}
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          selected={filterType === "Submitted" ? true : false}
          onClick={async (e) => {
            setFilterType("Submitted");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("submitted")}{" "}
          {event?.format?.teamEvent && !event?.format?.doublesEvent && t("team")}{" "}
          {t("pairings")}
        </MenuItem>

        <MenuItem
          selected={filterType === "Unsubmitted" ? true : false}
          onClick={async (e) => {
            setFilterType("Unsubmitted");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("unsubmitted")}{" "}
          {event?.format?.teamEvent && !event?.format?.doublesEvent && t("team")}{" "}
          {t("pairings")}
        </MenuItem>

        {event?.format?.teamEvent && !event?.format?.doublesEvent && (
          <>
            <Divider
              sx={{
                mt: 2,
                mb: 2
              }}
            />
            <MenuItem
              selected={filterType === "Teams With Pairings" ? true : false}
              onClick={async (e) => {
                setFilterType("Teams With Pairings");
                setAnchorEl(null);
              }}
              disableRipple
            >
              {t("teamsWithPairings")}
            </MenuItem>

            <MenuItem
              selected={filterType === "Teams Without Pairings" ? true : false}
              onClick={async (e) => {
                setFilterType("Teams Without Pairings");
                setAnchorEl(null);
              }}
              disableRipple
            >
              {t("teamsWithoutPairings")}
            </MenuItem>
          </>
        )}
      </StyledMenu>
    </>
  );
}
